<script>
// House side-panel shell: right-hand b-sidebar with the standard header
// (icon chip + title + close), scrollable body and optional pinned footer.
// Feature panels supply content via slots and scope their own styles through
// `panel-class`.
export default {
  name: 'AppSidePanel',
  props: {
    value: { type: Boolean, default: false },
    title: { type: String, required: true },
    width: { type: String, default: '480px' },
    panelClass: { type: String, default: '' },
    sidebarId: { type: String, default: null },
    plainIcon: { type: Boolean, default: false },
    // Destroys slot content while closed (b-modal-like lifecycle) so panels
    // whose children capture state in mounted() get a fresh mount per open.
    lazy: { type: Boolean, default: false },
    // Shows a ← button in the header (emits 'back') for in-panel navigation.
    showBack: { type: Boolean, default: false },
  },
  computed: {
    isOpen: {
      get() { return this.value; },
      set(val) { this.$emit('input', val); },
    },
    panelId() {
      return this.sidebarId || `app-side-panel-${this._uid}`;
    },
  },
};
</script>

<template>
  <b-sidebar :id="panelId"
             v-model="isOpen"
             right
             backdrop
             no-header
             :lazy="lazy"
             :width="width"
             bg-variant="white"
             :sidebar-class="['app-side-panel', 'tw-transition-all tw-duration-300 tw-ease-in-out motion-reduce:tw-transition-none', panelClass]"
  >
    <div class="tw-flex tw-flex-col tw-h-full">
      <div class="modal-header app-side-panel__header">
        <div class="tw-min-w-0 tw-pr-3 tw-flex tw-items-center tw-gap-3">
          <button v-if="showBack" type="button" class="app-side-panel__back" @click="$emit('back')">
            <i class="fe-arrow-left" />
          </button>
          <span v-if="$slots.icon" class="app-side-panel__icon" :class="{ 'app-side-panel__icon--plain': plainIcon }">
            <slot name="icon" />
          </span>
          <h4 class="!tw-my-0 tw-text-primary tw-font-semibold">{{ title }}</h4>
          <slot name="title-aside" />
        </div>
        <button type="button" class="close" :aria-label="$t('close')" @click="isOpen = false">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>

      <div class="tw-flex-1 tw-overflow-y-auto tw-p-4 tw-space-y-4">
        <slot />
      </div>

      <div v-if="$slots.footer" class="app-side-panel__footer">
        <slot name="footer" />
      </div>
    </div>
  </b-sidebar>
</template>

<style lang="scss">
.b-sidebar-outer:has(.app-side-panel) {
  z-index: 1060;
}

// Floating-sheet inset/rounding comes from the app-wide right-sidebar rules
// in design/custom/components/_side-panel.scss.
.app-side-panel {
  &__header {
    border-bottom: 0;
    align-items: center;
    padding-bottom: 0.5rem;
  }

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 10px;
    background: rgba($color_primary, 0.08);
    color: $color_primary;
    font-size: 1rem;
    flex-shrink: 0;

    &--plain {
      background: $color_light_bg;
    }
  }

  &__back {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 10px;
    border: 0;
    background: $color_light_bg;
    color: $color_text;
    flex-shrink: 0;

    &:hover {
      background: rgba($color_primary, 0.08);
      color: $color_primary;
    }
  }

  &__footer {
    padding: 0.75rem 1rem;
    background: #fff;
  }
}
</style>
